import { Container, ErrorBoundary } from '@packages/shared';
import useSWR from 'swr';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { remotePromotion } from '@packages/cms-components/src/atoms';
import { decodeFromBase64 } from '@packages/cms-components/src/utils/decodeFromBase64';
import { PromotionBannerTestIds } from './PromotionBannerTestIds';
import { PromotionBannerContentWrapper } from './PromotionBannerContentWrapper';
import type { PromotionVariantsData, PromotionBannerProps } from './types';
import { fetchAndActivatePromotion } from './utils/fetchAndActivatePromotion';

export const usePromotionBannerData = (
  fallbackPromotionData: PromotionVariantsData | undefined = undefined,
) => {
  const { data } = useSWR<PromotionVariantsData>('promotionBannerData', {
    fallbackData: fallbackPromotionData,
  });
  return { data, endpoint: 'promotionBannerData' };
};

const PromotionBannerContent = ({ promotionData }: { promotionData?: PromotionVariantsData }) => {
  // ESI-App, Tests and Storybook deliver the data by component-property - all other apps are feeding the swr-cache. So we try both sources here.
  const { data } = usePromotionBannerData(promotionData);

  // for NJS-1196 allow redeem voucher by internal SPA-link
  const { query } = useRouter();
  const setVoucher = useSetAtom(remotePromotion);
  useEffect(() => {
    const promotionCode =
      query.BonusNumber?.toString() || (query.P1 && decodeFromBase64(query.P1.toString()));
    if (promotionCode) {
      fetchAndActivatePromotion(promotionCode, setVoucher);
    }
  }, [query, setVoucher]);

  // only render anything if at least one data sample exists
  if (data && (data.shop || data.active || data.selected)) {
    return (
      <Container
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          minHeight: '4.5rem',
          textAlign: 'center',
          paddingBlock: 2 * 0.75,
          lineHeight: 1.5,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <PromotionBannerContentWrapper promotionData={data} />
      </Container>
    );
  }
  return <div data-testid={PromotionBannerTestIds.empty} />;
};

/**
 * The Promotionbanner-Component displays a colored promotion box with interactive features to enable a served promotion
 *
 * @param promotionData Object of shop, active and/or selected promotion data
 * - shop: promotionData by shopId - the corresponding id for e.g. storefront, damenmode, ...
 * - active: promotionData by currently user-active bonuscode (read from session/cookie)
 * - selected: promotionData by user-selected bonuscode - this can be query-Params ?BonusCode or ?P1 (base64 encoded bonuscode)
 * @returns JSX.Element to be placed in PageLayout
 */
export const PromotionBanner = ({ promotionData }: PromotionBannerProps): JSX.Element => (
  <ErrorBoundary fallback={<div data-testid={PromotionBannerTestIds.empty} />}>
    <PromotionBannerContent promotionData={promotionData} />
  </ErrorBoundary>
);
